import { Schemas } from "@kollectivia/shared";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FormRenderer } from "../../../components/forms/FormRenderer";
import { useAuth } from "../../../contexts/AuthContext";
import { api } from "../../../lib/api";
import { getApiErrorMessage } from "../../../lib/apiErrorMessage";
import type { FormResponseRow } from "../../../lib/queries/formResponses";

import { toWireData } from "./fillData";

type Props = {
  schema: Schemas.FormVersionSchemaType;
  versionId: string;
  responseId: string | null;
  revision: string | undefined;
  dirty: boolean;
  disabled: boolean;
  onBusy: (value: boolean) => void;
  onApplied: (row: FormResponseRow) => void;
};
const button =
  "min-h-11 rounded-lg border border-slate-300 px-3 py-2 text-sm text-brand-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50";
const inputClass =
  "mt-1 block min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

export function ScenarioPanel(props: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { user, tenant } = useAuth();
  if (!Schemas.isBusinessSpec(props.schema)) return null;
  return (
    <section className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={button}
        disabled={props.disabled}
      >
        {t("scenarios.title")}
      </button>
      <p className="mt-2 text-sm text-slate-600">{t("scenarios.intro")}</p>
      {open ? (
        <ScenarioWorkspace key={`${tenant?.id}:${user?.id}:${props.versionId}`} {...props} />
      ) : null}
    </section>
  );
}

function ScenarioWorkspace(props: Props) {
  const { t, i18n } = useTranslation();
  const language = i18n.language.startsWith("en") ? "en" : "fr";
  const { user, tenant } = useAuth();
  const qc = useQueryClient();
  const admin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [pending, setPending] = useState(false);
  const [selected, setSelected] = useState<Schemas.FormScenarioRevision | null>(null);
  const [keys, setKeys] = useState<string[]>([]);
  const [replace, setReplace] = useState<string[]>([]);
  const [preview, setPreview] = useState<ReturnType<typeof Schemas.previewFormScenario> | null>(
    null,
  );
  const [name, setName] = useState("");
  const [authoring, setAuthoring] = useState(false);
  const [baseId, setBaseId] = useState<string | undefined>();
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const blocked = busy || props.disabled;
  const queryKey = ["form-scenarios", tenant?.id, user?.id, props.versionId, pending];
  const list = useInfiniteQuery({
    queryKey,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api.get<{ items: Schemas.FormScenarioRevision[]; next_cursor: string | null }>(
        `/v1/form-response-scenarios?${new URLSearchParams({
          template_version_id: props.versionId,
          view: pending ? "pending" : "available",
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
  const eligible = props.schema.sections
    .flatMap((s) => s.fields)
    .filter((f) => f.scenario_parameter);
  const fields = new Map(eligible.map((f) => [f.key, f]));
  const display = (key: string, value: unknown): string => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") return t(value ? "scenarios.yes" : "scenarios.no");
    if (typeof value === "number")
      return new Intl.NumberFormat(language, { maximumFractionDigits: 20 }).format(value);
    const field = fields.get(key);
    const option = (v: unknown) =>
      field?.options?.find((o) => o.value === v)?.label[language] ?? String(v);
    return Array.isArray(value) ? value.map(option).join(", ") : option(value);
  };
  const parameterSchema = useMemo(
    () => ({
      ...props.schema,
      sections: props.schema.sections
        .map((s) => ({
          key: s.key,
          label: s.label,
          fields: s.fields
            .filter((f) => f.scenario_parameter)
            .map((f) => ({
              ...f,
              required: false,
              active_when: undefined,
              required_when: undefined,
              ...(f.type === "checkbox"
                ? {
                    type: "select" as const,
                    options: [
                      { value: "true", label: { fr: "Oui", en: "Yes" } },
                      { value: "false", label: { fr: "Non", en: "No" } },
                    ],
                  }
                : {}),
            })),
        }))
        .filter((s) => s.fields.length > 0),
    }),
    [props.schema],
  );
  useEffect(() => {
    setPreview(null);
  }, [props.revision, props.dirty, props.versionId]);
  async function run(action: () => Promise<void>) {
    if (blocked) return;
    setBusy(true);
    props.onBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (e) {
      setError(getApiErrorMessage(e, language) || t("scenarios.error"));
    } finally {
      setBusy(false);
      props.onBusy(false);
    }
  }
  function choose(row: Schemas.FormScenarioRevision) {
    setSelected(row);
    setKeys([]);
    setReplace([]);
    setPreview(null);
    setError(null);
  }
  function toggle(key: string, replacing = false) {
    setPreview(null);
    if (replacing)
      setReplace((old) => (old.includes(key) ? old.filter((x) => x !== key) : [...old, key]));
    else {
      setKeys((old) => (old.includes(key) ? old.filter((x) => x !== key) : [...old, key]));
      setReplace((old) => old.filter((x) => x !== key));
    }
  }
  const body = () => ({
    scenario_revision_id: selected!.id,
    expected_response_revision: props.revision,
    selected_keys: keys,
    replace_keys: replace,
  });
  async function sharing(action: "request" | "approve" | "reject" | "withdraw") {
    if (!selected) return;
    const row = await api.post<Schemas.FormScenarioRevision>(
      `/v1/form-response-scenarios/${selected.id}/sharing`,
      { action, expected_revision: selected.revision },
    );
    choose(row);
    await qc.invalidateQueries({ queryKey: ["form-scenarios"] });
    setNotice(t("scenarios.sharing_saved"));
  }
  const owner = selected?.owner_user_id === user?.id;
  return (
    <div className="mt-4 space-y-4">
      {error || list.error ? (
        <p role="alert" className="break-words text-sm text-red-700">
          {error || getApiErrorMessage(list.error, language) || t("scenarios.error")}
          {list.error ? (
            <button type="button" className={button} onClick={() => void list.refetch()}>
              {t("scenarios.retry")}
            </button>
          ) : null}
        </p>
      ) : null}
      {notice ? (
        <p role="status" className="text-sm text-emerald-700">
          {notice}
        </p>
      ) : null}
      {busy || list.isPending ? (
        <p role="status" className="text-sm">
          {t("scenarios.loading")}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {admin ? (
          <button
            type="button"
            disabled={blocked}
            className={button}
            onClick={() => {
              setPending(!pending);
              setSelected(null);
              setPreview(null);
            }}
          >
            {t(pending ? "scenarios.available" : "scenarios.pending")}
          </button>
        ) : null}
        <button
          type="button"
          disabled={blocked || eligible.length === 0}
          className={button}
          onClick={() => {
            setAuthoring(true);
            setBaseId(undefined);
            setSettings({});
            setName("");
          }}
        >
          {t("scenarios.create")}
        </button>
      </div>
      {eligible.length === 0 ? <p className="text-sm">{t("scenarios.no_parameters")}</p> : null}
      <ul className="space-y-2">
        {list.data?.pages
          .flatMap((p) => p.items)
          .map((row) => (
            <li key={row.id}>
              <button
                type="button"
                disabled={blocked}
                className={`${button} w-full break-words text-left`}
                aria-pressed={selected?.id === row.id}
                onClick={() => choose(row)}
              >
                {row.name} · {t("scenarios.version", { number: row.version_number })} ·{" "}
                {t(`scenarios.status.${row.sharing_status}`)}
              </button>
            </li>
          ))}
      </ul>
      {list.data?.pages.every((p) => p.items.length === 0) ? (
        <p className="text-sm">{t("scenarios.empty")}</p>
      ) : null}
      {list.hasNextPage ? (
        <button
          type="button"
          className={button}
          disabled={blocked || list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          {t("scenarios.more")}
        </button>
      ) : null}

      {authoring ? (
        <fieldset
          disabled={blocked}
          className="space-y-3 rounded-lg border border-slate-200 bg-white p-3"
        >
          <legend className="text-sm font-medium">
            {t(baseId ? "scenarios.new_revision" : "scenarios.create")}
          </legend>
          <label className="block text-sm">
            {t("scenarios.name")}
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              className={inputClass}
            />
          </label>
          <p className="text-sm text-slate-600">{t("scenarios.authoring_help")}</p>
          <FormRenderer
            schema={parameterSchema}
            locale={language}
            mode="fill"
            values={Object.fromEntries(
              Object.entries(settings).map(([k, v]) => [k, typeof v === "boolean" ? String(v) : v]),
            )}
            onValuesChange={setSettings}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={button}
              onClick={() =>
                void run(async () => {
                  const typed = { ...settings };
                  for (const f of eligible)
                    if (
                      f.type === "checkbox" &&
                      (typed[f.key] === "true" || typed[f.key] === "false")
                    )
                      typed[f.key] = typed[f.key] === "true";
                  const parameters = Schemas.validateScenarioParameters(
                    props.schema,
                    toWireData(typed, props.schema),
                  );
                  const row = await api.post<Schemas.FormScenarioRevision>(
                    "/v1/form-response-scenarios",
                    {
                      template_version_id: props.versionId,
                      scenario_id: baseId,
                      name,
                      parameters,
                    },
                  );
                  setAuthoring(false);
                  choose(row);
                  await qc.invalidateQueries({ queryKey: ["form-scenarios"] });
                  setNotice(t("scenarios.created"));
                })
              }
            >
              {t("scenarios.save_private")}
            </button>
            <button type="button" className={button} onClick={() => setAuthoring(false)}>
              {t("scenarios.cancel")}
            </button>
          </div>
        </fieldset>
      ) : null}

      {selected ? (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
          <h3 className="break-words font-medium">
            {selected.name} · {t("scenarios.version", { number: selected.version_number })}
          </h3>
          <p className="text-sm">{t(`scenarios.status.${selected.sharing_status}`)}</p>
          <div className="flex flex-wrap gap-2">
            {owner ? (
              <button
                className={button}
                type="button"
                disabled={blocked}
                onClick={() => {
                  setBaseId(selected.scenario_id);
                  setSettings(selected.parameters);
                  setName(selected.name);
                  setAuthoring(true);
                }}
              >
                {t("scenarios.new_revision")}
              </button>
            ) : null}
            {owner && selected.sharing_status === "private" ? (
              <button
                className={button}
                type="button"
                disabled={blocked}
                onClick={() => void run(() => sharing("request"))}
              >
                {t("scenarios.request")}
              </button>
            ) : null}
            {admin && selected.sharing_status === "pending" ? (
              <>
                <button
                  className={button}
                  type="button"
                  disabled={blocked}
                  onClick={() => void run(() => sharing("approve"))}
                >
                  {t("scenarios.approve")}
                </button>
                <button
                  className={button}
                  type="button"
                  disabled={blocked}
                  onClick={() => void run(() => sharing("reject"))}
                >
                  {t("scenarios.reject")}
                </button>
              </>
            ) : null}
            {(owner && selected.sharing_status === "pending") ||
            ((owner || admin) && selected.sharing_status === "approved") ? (
              <button
                className={button}
                type="button"
                disabled={blocked}
                onClick={() => void run(() => sharing("withdraw"))}
              >
                {t("scenarios.withdraw")}
              </button>
            ) : null}
          </div>
          <p className="text-sm text-slate-600">{t("scenarios.selection_help")}</p>
          {Object.entries(selected.parameters).map(([key, value]) => (
            <div key={key} className="space-y-1 border-t border-slate-100 pt-2">
              <label className="flex min-h-11 items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={keys.includes(key)}
                  disabled={blocked}
                  onChange={() => toggle(key)}
                  className="focus-visible:ring-brand-500 mt-1 h-4 w-4 focus-visible:ring-2"
                />
                <span className="min-w-0 break-words">
                  {fields.get(key)?.label[language] ?? key} : {display(key, value)}
                </span>
              </label>
              {keys.includes(key) ? (
                <label className="flex min-h-11 items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={replace.includes(key)}
                    disabled={blocked}
                    onChange={() => toggle(key, true)}
                    className="focus-visible:ring-brand-500 mt-1 h-4 w-4 focus-visible:ring-2"
                  />
                  <span>
                    {t("scenarios.replace", { field: fields.get(key)?.label[language] ?? key })}
                  </span>
                </label>
              ) : null}
            </div>
          ))}
          {!props.responseId || !props.revision || props.dirty ? (
            <p role="status" className="text-sm text-amber-800">
              {t("scenarios.save_first")}
            </p>
          ) : null}
          <button
            type="button"
            className={button}
            disabled={
              blocked ||
              !props.responseId ||
              !props.revision ||
              props.dirty ||
              keys.length === 0 ||
              (!owner && selected.sharing_status !== "approved")
            }
            onClick={() =>
              void run(async () => {
                setPreview(
                  await api.post(
                    `/v1/form-response-scenarios/responses/${props.responseId}/preview`,
                    body(),
                  ),
                );
              })
            }
          >
            {t("scenarios.preview")}
          </button>
          {preview ? (
            <div aria-live="polite" className="space-y-2">
              {preview.changes
                .filter((c) => c.selected)
                .map((c) => (
                  <p key={c.key} className="break-words text-sm">
                    {fields.get(c.key)?.label[language] ?? c.key} : {display(c.key, c.current)} →{" "}
                    {display(c.key, c.proposed)}
                    {" · "}
                    {t(
                      !c.active
                        ? "scenarios.inactive"
                        : c.conflict && !c.applied
                          ? "scenarios.kept"
                          : c.applied
                            ? "scenarios.changed"
                            : "scenarios.unchanged",
                    )}
                  </p>
                ))}
              {!preview.changes.some((change) => change.applied) ? (
                <p role="status">{t("scenarios.no_changes")}</p>
              ) : null}
              <button
                className={button}
                type="button"
                disabled={
                  blocked || props.dirty || !preview.changes.some((change) => change.applied)
                }
                onClick={() =>
                  void run(async () => {
                    const row = await api.post<FormResponseRow>(
                      `/v1/form-response-scenarios/responses/${props.responseId}/apply`,
                      body(),
                    );
                    props.onApplied(row);
                    setPreview(null);
                    setNotice(t("scenarios.applied"));
                  })
                }
              >
                {t("scenarios.apply")}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
